import { ResultOf, readFragment } from "gql.tada";
import { useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { RoutingSectionById } from "@queries/entries/RoutingSectionById";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { Button } from "@components/shared/core/Button";
import { Link } from "@components/shared/core/Link";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Analytics } from "@utils/Analytics";
import { useGlobalContext } from "@utils/GlobalContext";

type Entry = ResultOf<typeof RoutingSectionById>;

export const StillWatchingRoutingSection = ({
  entry: { routingSection: entry },
}: {
  entry: Entry;
}) => {
  const { locale } = useGlobalContext();
  const link = readFragment(LinkFragment, entry?.cta);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const value = useTransform(scrollYProgress, [0, 0.5, 1], [-1, 0, 0]);

  const handleCtaClick = () => {
    Analytics.trackEvent(`still_watching_${locale}_report_cta_click`);
  };

  if (!entry) return null;

  return (
    <div
      ref={containerRef}
      className="h-[694px] dsk:h-[850px] px-[18px] py-[60px] dsk:px-8 dsk:py-[38px] relative"
    >
      <section className="h-full bg-cover bg-center bg-no-repeat rounded-xl overflow-hidden relative flex items-center justify-center flex-row">
        <img
          className="absolute inset-0 brightness-60 object-cover size-full"
          src={(entry.backgroundImage && typeof entry.backgroundImage === 'object' && 'url' in entry.backgroundImage && typeof entry.backgroundImage.url === 'string') ? entry.backgroundImage.url : ""}
        />
        <div className="flex dsk:px-[98px] p-6 justify-between h-full flex-col dsk:flex-row dsk:items-center dsk:w-full">
          <div className="text-brand-cream text-heading-1 self-start dsk:self-auto">
            {entry.title && (
              <h2>
                <AnimatedSplitLines value={value}>
                  {entry.title}
                </AnimatedSplitLines>
              </h2>
            )}
          </div>
          {link && (
            <div className="self-center dsk:self-auto">
              <Button
                asChild
                variant="light-on-blur"
                className="w-max"
              >
                <Link link={link} onClick={handleCtaClick}>{link.text}</Link>
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
